import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useGetCriticalIncidentReferrals } from 'api/queries/criticalIncidentQuery';
import { useInterval } from 'hooks/useInterval/useInterval';
import { type ProviderData, useAuth } from 'components/Auth';
import { sortCIReferrals } from 'components/CriticalIncident/utils/CIReferralUtils';
import { CIReferralDecisionModal } from '../CIReferralDecisionModal';
import { CITodoCard, CITodoCardSkeleton } from '../CITodoCard';
import type { CIReferral } from 'components/CriticalIncident/interfaces';

import './CIReferralsGrid.scss';

export const CIReferralsGrid: React.FC = () => {
    const { t, i18n } = useTranslation('criticalIncident', { useSuspense: false });
    const { effectiveProviderData = {} as ProviderData } = useAuth();
    const [params, setParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(!!params.get('referralId'));
    const { data: referrals, isFetching, refetch, dataUpdatedAt = null } = useGetCriticalIncidentReferrals();
    const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null);
    
    const timeElapsed = React.useCallback(() => {
        if (!dataUpdatedAt) return '';
        moment.locale(i18n.language);
        const diff = moment().diff(moment(dataUpdatedAt), 'milliseconds');
        const duration = moment.duration(Math.abs(diff) * -1);
        return duration.humanize(true);
    }, [dataUpdatedAt, i18n.language]);

    useInterval(() => {
        if (dataUpdatedAt) setLastUpdatedAt(timeElapsed());
    }, 60 * 1000, true);
    
    const closeCIReferralModal = () => {
        params?.delete('referralId');
        setParams(params);
        setIsOpen(false);
    }

    useEffect(() => {
        if (params.get('referralId')) {
            setIsOpen(true);
        }
        if (dataUpdatedAt) {
            setLastUpdatedAt(timeElapsed());
        }
    }, [params, i18n.language, dataUpdatedAt, timeElapsed]);

    const filteredReferrals = useMemo(() => {
        return sortCIReferrals(referrals || []);
    }, [referrals]);

    return (
        <>
            <div className="ci_referrals_grid">
                <div className="grid_header">
                    <div className="grid_label">
                        <h2>{t('referrals')}</h2>
                    </div>
                    <div className="grid_actions">
                        <Link className="viewAll__link" to={{ pathname: '/critical_incident/referrals' }}>
                            {t('view_all')} ({ filteredReferrals?.length || 0 })
                        </Link>
                    </div>
                </div>
                <Grid className="grid_body">
                    {isFetching ? Array.from({ length: filteredReferrals?.length || 10 }, (_, index) => (
                        <Grid item key={index}>
                            <CITodoCardSkeleton />
                        </Grid>
                    )) : (
                        <>
                            {!filteredReferrals?.length && <div className="no_referrals_message">{t('you_dont_have_any_ci_referrals')}</div>}
                            {filteredReferrals?.map(referral => (
                                <Grid item key={referral.referralId}>
                                    <CITodoCard referral={referral as CIReferral} reloadGrid={refetch} />
                                </Grid>
                            ))}
                        </>
                    )}
                </Grid>
                <div className="grid_footer">
                    <Link className="refresh_link" onClick={() => refetch()} to={{ pathname: window.location.pathname }}>
                        <RefreshIcon />{t('refresh')}
                    </Link>
                    {lastUpdatedAt && <span className="updated_time"> •&nbsp;&nbsp;{t('updated_at', { lastUpdatedAt })}</span>}
                </div>
            </div>            
            {params.get('referralId') && (
                <CIReferralDecisionModal
                    open={isOpen}
                    onClose={closeCIReferralModal}
                    providerId={effectiveProviderData!.providerId}
                    referralId={params.get('referralId') ?? ''}
                />
            )}
        </>
    );
};
